import { Component } from '@theme/component';

/**
 * Announcement banner custom element with marquee functionality.
 * Automatically calculates optimal display duration based on content width.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The timeout ID for automatic playback.
   * @type {number|undefined}
   */
  #timeout = undefined;

  /**
   * Pixels per second for marquee scrolling.
   * @type {number}
   */
  #marqueeSpeed = 25;

  /**
   * Timeout ID for debounced resize handling.
   * @type {number|undefined}
   */
  #resizeTimeout = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    window.addEventListener('resize', this.#handleResize);

    // Initialize marquee for all slides (always needed)
    this.#initializeMarquees();

    // Only start autoplay if there are multiple slides and autoplay is enabled
    if (this.autoplay) {
      this.play();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    window.removeEventListener('resize', this.#handleResize);
    clearTimeout(this.#resizeTimeout);
  }

  /**
   * Initialize marquee structure and animations for all slides
   */
  #initializeMarquees() {
    this.refs.slides?.forEach((slide) => {
      const textElement = slide.querySelector('.announcement-bar__text');
      if (!textElement) return;

      // Wrap text in marquee structure if not already done
      if (!slide.querySelector('.marquee-wrapper')) {
        const wrapper = document.createElement('div');
        wrapper.className = 'marquee-wrapper';

        const content = document.createElement('div');
        content.className = 'marquee-content';

        // Move text into marquee structure (no duplication - single display)
        content.appendChild(textElement.cloneNode(true));
        wrapper.appendChild(content);

        // Replace original text with marquee structure
        textElement.replaceWith(wrapper);
      }

      // Check if scrolling is needed and set animation duration
      this.#updateMarqueeBehavior(slide);
    });
  }

  /**
   * Calculate marquee duration for a slide based on content width
   * @param {HTMLElement} slide - The slide element
   * @returns {number} Duration in milliseconds
   */
  #calculateMarqueeDuration(slide) {
    const wrapper = /** @type {HTMLElement | null} */ (slide.querySelector('.marquee-wrapper'));
    const content = /** @type {HTMLElement | null} */ (slide.querySelector('.marquee-content'));

    if (!wrapper || !content) return this.autoplayInterval || 5000;

    // Get the available container width (from the slides container)
    const slidesContainer = /** @type {HTMLElement | null} */ (slide.closest('.announcement-bar__slides'));
    const containerWidth = slidesContainer?.offsetWidth || wrapper.offsetWidth;

    // Measure content width without padding (temporarily remove padding classes)
    const originalClasses = content.className;
    content.className = content.className.replace(/marquee-wrapper--scrolling/, '');
    const contentWidth = content.scrollWidth;
    content.className = originalClasses;

    // Calculate total padding dynamically from CSS
    const computedStyle = window.getComputedStyle(content);
    const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
    const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
    const totalPadding = paddingLeft + paddingRight;

    // If content + padding fits in container, no scrolling needed
    if (contentWidth + totalPadding <= containerWidth) {
      return this.autoplayInterval || 5000;
    }

    // Calculate time needed to scroll the overflow amount
    // Scroll distance = content width + padding - container width
    const scrollDistance = contentWidth + totalPadding - containerWidth;
    const marqueeDuration = (scrollDistance / this.#marqueeSpeed) * 1000;

    return marqueeDuration;
  }

  /**
   * Update marquee behavior: enable scrolling only if content overflows
   * @param {HTMLElement} slide - The slide element
   */
  #updateMarqueeBehavior(slide) {
    const wrapper = /** @type {HTMLElement | null} */ (slide.querySelector('.marquee-wrapper'));
    const content = /** @type {HTMLElement | null} */ (slide.querySelector('.marquee-content'));

    if (!wrapper || !content) return;

    // Get the available container width (from the slides container)
    const slidesContainer = /** @type {HTMLElement | null} */ (slide.closest('.announcement-bar__slides'));
    const containerWidth = slidesContainer?.offsetWidth || wrapper.offsetWidth;

    // Measure content width without padding (temporarily remove padding classes)
    const originalClasses = content.className;
    content.className = content.className.replace(/marquee-wrapper--scrolling/, '');
    const contentWidth = content.scrollWidth;
    content.className = originalClasses;

    // Calculate total padding dynamically from CSS
    const computedStyle = window.getComputedStyle(content);
    const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
    const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
    const totalPadding = paddingLeft + paddingRight;

    // Calculate total content width WITH padding
    const totalContentWidth = contentWidth + totalPadding;

    // Set the container width for CSS calculations
    slide.style.setProperty('--container-width', `${containerWidth}px`);

    // Check if content + padding overflows
    const needsScroll = totalContentWidth > containerWidth;

    // Add or remove scrolling class
    wrapper.classList.toggle('marquee-wrapper--scrolling', needsScroll);

    if (needsScroll) {
      // Calculate scroll distance (how much to move left to show right padding)
      const scrollDistance = totalContentWidth - containerWidth;
      slide.style.setProperty('--scroll-distance', `${scrollDistance}px`);

      // Calculate and set animation duration
      const marqueeDuration = this.#calculateMarqueeDuration(slide);
      const durationSeconds = marqueeDuration / 1000;
      slide.style.setProperty('--marquee-duration', `${durationSeconds}s`);
    } else {
      // No scrolling needed, content is centered
      slide.style.removeProperty('--marquee-duration');
      slide.style.removeProperty('--scroll-distance');
    }
  }

  /**
   * Get the display duration for the current slide
   * Uses Math.max(configuredSpeed, marqueeDuration)
   * @returns {number} Duration in milliseconds
   */
  #getCurrentSlideDuration() {
    const currentSlide = this.#getCurrentSlide();
    if (!currentSlide) return this.autoplayInterval || 5000;

    const marqueeDuration = this.#calculateMarqueeDuration(currentSlide);
    const configuredDuration = this.autoplayInterval || 5000;

    // Use the maximum of the two durations
    return Math.max(configuredDuration, marqueeDuration);
  }

  /**
   * Get the currently visible slide element
   * @returns {HTMLElement|null}
   */
  #getCurrentSlide() {
    const slides = this.refs.slides ?? [];
    let relativeIndex = this.#current % slides.length;
    if (relativeIndex < 0) {
      relativeIndex += slides.length;
    }
    return slides[relativeIndex] || null;
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback with smart duration calculation.
   */
  play() {
    if (!this.autoplay) return;

    this.paused = false;

    // Clear any existing timeout
    this.suspend();

    // Schedule next slide transition based on current slide's optimal duration
    const duration = this.#getCurrentSlideDuration();

    this.#timeout = setTimeout(() => {
      if (this.matches(':hover') || document.hidden) {
        this.play(); // Reschedule
        return;
      }

      this.next();
      this.play(); // Schedule next transition
    }, duration);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearTimeout(this.#timeout);
    this.#timeout = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval && this.autoplayInterval > 0);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slideElement, index) => {
      const slide = /** @type {HTMLElement} */ (slideElement);
      const isActive = index === relativeIndex;
      slide.setAttribute('aria-hidden', `${!isActive}`);

      // Restart marquee animation when slide becomes visible
      if (isActive) {
        const content = /** @type {HTMLElement | null} */ (slide.querySelector('.marquee-content'));
        if (content) {
          // Force animation restart
          content.style.animation = 'none';
          void content.offsetHeight; // Trigger reflow
          content.style.animation = '';
        }
      }
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());

  /**
   * Handle window resize events with debouncing.
   */
  #handleResize = () => {
    clearTimeout(this.#resizeTimeout);
    this.#resizeTimeout = setTimeout(() => {
      this.#initializeMarquees();
    }, 250);
  };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
